import { internalLogger } from '../../../interface/v1/clientOsLogger';
import {
  EventServicePlugin,
  EventServicePluginError,
  JWebWindow,
  Publisher,
  Subscriber
} from '../types';
import { jWebStates } from '../Initializer';
import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';
import { withTimeout } from '../../../utils/withTimeout';

/**
 * Facade for EventService operations
 */
export class EventServiceFacade {
  /**
   * Get the EventService plugin
   */
  private static async getEventServicePlugin(): Promise<
    EventServicePlugin | undefined
  > {
    await this.isEventServiceReady();

    const windowEventService: EventServicePlugin = (window as JWebWindow)?.JWeb
      ?.Plugins?.EventService;
    if (!windowEventService) {
      internalLogger?.error(
        'Unable to obtain EventService (missing some JWeb plugin)'
      );
    }
    return windowEventService;
  }

  /**
   * Checks if the EventService plugin is ready for use
   */
  private static async isEventServiceReady(): Promise<boolean> {
    // Is Jweb ready?
    await jWebStates.JWebLibrary;

    // Is the plugins registered?
    await jWebStates.defaultPluginsRegistered;

    // Was valueStore installed?
    await jWebStates.plugins.EventService;
    return true;
  }

  /**
   * Create a Publisher instance
   */
  @loggerDecorator.debug.d3()
  public static async createPublisher(publisherId: string): Promise<Publisher> {
    const eventServicePlugin = await this.getEventServicePlugin();

    const publisherOrError = await withTimeout(
      eventServicePlugin.createPublisher(publisherId),
      {
        message: 'Timeout: EventService.createPublisher() failed'
      }
    );

    // TODO: Create a JWeb utility method to handle cases when an 'EventServicePluginError' is returned
    if (
      publisherOrError &&
      typeof publisherOrError === 'object' &&
      'errorType' in publisherOrError
    ) {
      throw publisherOrError;
    }

    return publisherOrError as Publisher;
  }

  /**
   * Get a new Subscriber instance
   */
  @loggerDecorator.debug.d3()
  public static async getSubscribe(): Promise<Subscriber> {
    const eventServicePlugin = await this.getEventServicePlugin();

    const subscriberOrError = await withTimeout(
      eventServicePlugin.createSubscriber(),
      {
        message: 'Timeout: EventService.createSubscriber() failed'
      }
    );

    // TODO: Create a JWeb utility method to handle cases when an 'EventServicePluginError' is returned
    if (
      subscriberOrError &&
      typeof subscriberOrError === 'object' &&
      'errorType' in subscriberOrError
    ) {
      throw subscriberOrError;
    }

    return subscriberOrError as Subscriber;
  }

  /**
   * Create a Subscriber
   */
  @loggerDecorator.debug.d3()
  public static async createSubscriber(): Promise<
    Subscriber | EventServicePluginError
  > {
    const eventServicePlugin = await this.getEventServicePlugin();

    return withTimeout(eventServicePlugin.createSubscriber(), {
      message: 'Timeout: EventService.createSubscriber() failed'
    });
  }
}
